<template>
  <div class="permission-add">
    <div class="header">
      <div class="back" @click="back()">
        <img src="@/assets/icons/icon_fh.png" alt="" srcset="" />
        <span>{{ $t('roleManagement.back') }}</span>
      </div>
      <h4>{{ id ? $t('roleManagement.editRole') : $t('roleManagement.newRole') }}</h4>
    </div>

    <div class="content">
      <a-form-model ref="permissionForm" :model="formData" :rules="rules">
        <div class="form-item">
          <div class="required-label">{{ $t('roleManagement.roleName') }}：</div>
          <a-input v-model="formData.name" :placeholder="$t('roleManagement.inputRoleName')" :maxLength="30" class="name-input" />
        </div>

        <div class="permission-section">
          <div class="section-title">{{ $t('roleManagement.permissionSetting') }}</div>
          <div class="permission-list">
            <div class="permission-row" v-for="(group, index) in permissionGroups" :key="index">
              <div class="permission-group">
                <a-checkbox v-model="group.checked" @change="e => handleMainPermissionChange(e, group)">
                  {{ group.label }}
                </a-checkbox>
              </div>
              <div class="permission-items">
                <a-checkbox
                  v-for="child in group.children"
                  :key="child.value"
                  v-model="child.checked"
                  @change="e => handleSubPermissionChange(e, child, group)"
                >
                  {{ child.label }}
                </a-checkbox>
              </div>
            </div>
          </div>
        </div>

        <div class="user-section">
          <div class="section-title line">{{ $t('roleManagement.userManagement') }}</div>
          <div class="user-table">
            <l-tables
              ref="userTable"
              :inline="true"
              :content="'user'"
              :tableConfig="userTableConfig"
              :searchParams="keyword"
              :noCreatText="$t('roleManagement.noUser')"
              :ImgSrc="nullImg"
              :externalData="filteredUsers"
            >
              <template slot="searchConditions">
                <div class="search-conditions">
                  <a-input
                    :placeholder="$t('roleManagement.searchUserPlaceholder')"
                    v-model="keyword.name"
                    class="search-input"
                  >
                    <img slot="prefix" :src="require('@/assets/icons/icon_ss.png')" />
                    <img
                      class="clear"
                      v-if="keyword.name"
                      slot="suffix"
                      @click="clearSearch"
                      :src="require('@/assets/icons/icon_gb.png')"
                    />
                  </a-input>
                </div>
              </template>
              <template slot="searchActions">
                <a-button type="primary" @click="goToAdd">{{ $t('roleManagement.addUser') }}</a-button>
              </template>
              <template #operateCustom="{ record }">
                <a-button type="link" @click="handleUserAction(record)">{{ $t('roleManagement.remove') }}</a-button>
              </template>
            </l-tables>
          </div>
        </div>

        <div class="form-actions">
          <a-button type="primary" @click="handleSubmit" :loading="submitting">
            {{ $t('roleManagement.save') }}
          </a-button>
        </div>
      </a-form-model>
    </div>

    <add-user-modal
      :visible="addUserModalVisible"
      :selectedUserIds="selectedUserIds"
      :externalData="externalData"
      @cancel="handleAddUserCancel"
      @success="handleAddUserSuccess"
    />
  </div>
</template>

<script>
import LTables from '@/components/LTables/index.vue'
import AddUserModal from './components/AddUserModal.vue'
import { getRolePermissions, roleSave, roleDetail, getRoleUsers } from '@/api/admin'
import { i18nRender } from '@/locales'

export default {
  name: 'PermissionAdd',
  components: {
    LTables,
    AddUserModal
  },
  data() {
    return {
      id: this.$route.query.id,
      isEdit: false,
      submitting: false,
      formData: {
        name: '',
        permissions: [],
        users: []
      },
      rules: {
        name: [
          { required: true, message: i18nRender('roleManagement.roleNameRequired'), trigger: 'blur' },
          { max: 30, message: i18nRender('roleManagement.roleNameMaxLength'), trigger: 'blur' }
        ]
      },
      columns: [
        { title: i18nRender('roleManagement.teacherName'), dataIndex: 'name', key: 'name' },
        { title: i18nRender('roleManagement.teacherAccount'), dataIndex: 'account', key: 'account' },
        { title: i18nRender('roleManagement.status'), dataIndex: 'status', key: 'status', scopedSlots: { customRender: 'status' } },
        { title: i18nRender('roleManagement.operation'), key: 'action', scopedSlots: { customRender: 'action' } }
      ],
      userList: [],
      selectedUserIds: [],
      permissionGroups: [], // 改为空数组，由接口数据填充
      nullImg: require('@/assets/img/trainingRoomlistNull.png'),
      keyword: {
        name: ''
      },
      userTableConfig: {
        tableName: '',
        tableColumns: [
          {
            title: i18nRender('roleManagement.teacherName'),
            dataIndex: 'name',
            key: 'name',
            align: 'center'
          },
          {
            title: i18nRender('roleManagement.teacherAccount'),
            dataIndex: 'username',
            key: 'username',
            align: 'center'
          },
          {
            title: i18nRender('roleManagement.status'),
            dataIndex: 'status',
            key: 'status',
            align: 'center',
            customRender: text => (text === 1 ? i18nRender('roleManagement.enabled') : i18nRender('roleManagement.disabled'))
          },
          {
            title: i18nRender('roleManagement.operation'),
            dataIndex: 'operate',
            key: 'operate',
            width: 100,
            align: 'center',
            scopedSlots: { customRender: 'operateCustom' }
          }
        ],
        tablePort: {
          list: '/admin/role/user_list'
        }
      },
      addUserModalVisible: false,
      externalData: []
    }
  },
  computed: {
    filteredUsers() {
      if (!this.keyword.name) {
        return this.externalData
      }
      const searchText = this.keyword.name.toLowerCase()
      return this.externalData.filter(
        user =>
          user.name.toLowerCase().includes(searchText) ||
          (user.username && user.username.toLowerCase().includes(searchText))
      )
    }
  },
  async created() {
    await this.getPermissionOptions()
    const { id } = this.$route.query
    if (id) {
      this.isEdit = true
      this.getPermissionDetail(id)
      this.getUserList(id)
    }
  },
  methods: {
    back() {
      this.$router.back()
    },
    async getPermissionOptions() {
      try {
        const res = await getRolePermissions()
        if (res.data && res.data.list) {
          this.permissionGroups = res.data.list.map(group => ({
            label: group.name,
            value: group.id,
            checked: false,
            children: group.sub
              ? group.sub.map(child => ({
                  label: child.name,
                  value: child.id,
                  checked: false
                }))
              : []
          }))
        }
      } catch (error) {
        this.$message.error(this.$t('roleManagement.getPermissionOptionsFailed'))
      }
    },
    async getPermissionDetail(id) {
      try {
        const res = await roleDetail({ id })
        this.formData.name = res.data.name
        this.selectedUserIds = res.data.users || []
        this.initPermissions(res.data.permissions)
      } catch (error) {
        this.$message.error(this.$t('roleManagement.getRoleDetailFailed'))
      }
    },
    initPermissions(permissions) {
      permissions.forEach(permission => {
        this.permissionGroups.forEach(group => {
          if (group.children) {
            const childPermission = group.children.find(child => child.value === permission)
            if (childPermission) {
              childPermission.checked = true
              this.updateParentPermission(group)
            }
          }
        })
      })
    },
    handleMainPermissionChange(e, item) {
      e.stopPropagation() // 阻止事件冒泡
      const checked = e.target.checked
      if (item.children) {
        item.children.forEach(child => {
          child.checked = checked
        })
      }
    },
    handleSubPermissionChange(e, child, parent) {
      e.stopPropagation() // 阻止事件冒泡
      this.updateParentPermission(parent)
    },
    updateParentPermission(parent) {
      if (parent.children) {
        parent.checked = parent.children.every(child => child.checked)
      }
    },
    getSelectedPermissions() {
      const permissions = []
      this.permissionGroups.forEach(group => {
        if (group.children) {
          group.children.forEach(child => {
            if (child.checked) {
              permissions.push(child.value)
              permissions.push(group.value)
            }
          })
        }
      })
      // permissions去重
      const uniquePermissions = [...new Set(permissions)]
      return uniquePermissions
    },
    handleUserAction(record) {
      const userId = record.id
      this.externalData = this.externalData.filter(user => user.id !== userId)
      // this.$message.success('移除用户成功')
    },
    async handleSubmit() {
      try {
        await this.$refs.permissionForm.validate()
        this.submitting = true

        const permissions = this.getSelectedPermissions()
        const data = {
          id: this.$route.query.id,
          name: this.formData.name,
          permissions,
          users: this.externalData.map(user => user.id)
        }

        const res = await roleSave(data)
        if (res.code !== 200) {
          return
        }

        this.$message.success(this.isEdit ? this.$t('roleManagement.editSuccess') : this.$t('roleManagement.createSuccess'))
        this.goBack()
      } catch (error) {
        if (error.message) {
          this.$message.error(error.message)
        }
      } finally {
        this.submitting = false
      }
    },
    goBack() {
      this.$router.push({ name: 'systemPermission' })
    },
    // 获取角色下的用户列表
    async getUserList(id) {
      try {
        const res = await getRoleUsers({ id })
        if (res.code === 200) {
          this.externalData = res.data.list
        }
      } catch (error) {
        this.$message.error(this.$t('roleManagement.getUserListFailed'))
      }
    },
    goToAdd() {
      this.addUserModalVisible = true
    },
    handleAddUserCancel() {
      this.addUserModalVisible = false
    },
    handleAddUserSuccess(selectedUsers) {
      // 更新 externalData
      this.externalData = [...this.externalData, ...selectedUsers]

      this.addUserModalVisible = false
      this.$message.success(this.$t('roleManagement.addUserSuccess'))
      // 刷新用户表格
      this.$refs.userTable && this.$refs.userTable.refresh()
    },
    clearSearch() {
      this.keyword.name = ''
    }
  }
}
</script>

<style lang="less" scoped>
.back {
  width: 80px;
  height: 34px;
  display: flex;
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  color: #000000;
  border-radius: 25px;
  margin-bottom: 26px;
  align-items: center;
  justify-content: center;
  border: 1px solid #d6d6d6;

  img {
    height: 10px;
    margin-right: 9px;
  }
}
.permission-add {

  .header {
    padding: 16px 24px 0 24px;
    h4 {
      font-size: 16px;
      font-weight: 800;
      color: #000000;
      line-height: 22px;
    }
  }

  .content {
    padding: 24px;
  }

  .form-item {
    margin-bottom: 24px;
    // display: flex;
    // align-items: center;

    .required-label {
      position: relative;
      padding-left: 10px;
      margin-right: 8px;
      margin-bottom: 10px;

      &::before {
        content: '*';
        color: #f5222d;
        position: absolute;
        left: 0;
        top: 3px;
      }
    }

    .name-input {
      width: 400px;
    }
  }

  .section-title {
    font-weight: bold;
    margin-bottom: 16px;
    padding-left: 16px;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 4px;
      width: 4px;
      height: 12px;
      background: #6b738c;
      border-radius: 2px;
    }
  }
  .line{
    padding-bottom: 12px;
    border-bottom: 1px solid #E2E5E7;
  }

  .permission-section {
    margin-bottom: 32px;

    .permission-list {
      border: 1px solid #e8e8e8;
      border-radius: 4px;

      .permission-row {
        display: flex;
        border-bottom: 1px solid #e8e8e8;
        height: 51px;

        &:last-child {
          border-bottom: none;
        }

        .permission-group {
          width: 130px;
          padding: 16px;
          background-color: #fafafa;
          border-right: 1px solid #e8e8e8;
        }

        .permission-items {
          flex: 1;
          padding: 16px;
          display: flex;
          flex-wrap: wrap;

          .ant-checkbox-wrapper {
            margin-right: 24px;
            margin-bottom: 8px;
            min-width: 120px;
          }
        }
      }
    }
  }

  .user-section {
    margin-bottom: 32px;

    .search-box {
      margin-bottom: 16px;

      .search-input {
        width: 260px;
        /deep/ input {
          border-radius: 20px;
          border: 1px solid #d8dce0;
        }
        img {
          width: 16px;
          cursor: pointer;
        }
      }
    }
  }

  .form-actions {
    text-align: center;
    margin-top: 32px;
  }
}
/deep/ .selection {
  .ant-input-affix-wrapper, .ant-input{
    width: 300px;
  }
}
/deep/ .ant-table-header{
  overflow-y: auto !important;
}
</style>
